import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { Button } from '@openx/components-core/lib/Button/Button';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AudienceListType, FormSegment } from '../../../../../../../../modules/audiences/AudienceForm/types';
import { ListType } from '../../../../../../../../modules/audiences/AudienceForm/types';
import { LocationSegmentsListItem } from './LocationSegmentsListItem';

export type BaseLocationProps = {
  locationSegments: FormSegment[];
  listType: AudienceListType;
  dataTest?: string;
};

export type LocationSegmentsListProps =
  | (BaseLocationProps & { readOnly: true })
  | (BaseLocationProps & {
      readOnly?: false;
      onSegmentDelete: (segment: FormSegment) => void;
    });

export const LocationSegmentsList = ({ locationSegments, listType, dataTest, ...rest }: LocationSegmentsListProps) => {
  const { t } = useTranslation();

  const [showMoreItems, setShowMoreItems] = useState<boolean>(false);

  const countAllSegments = locationSegments?.length;

  const splitAllSegments = useMemo(() => {
    return countAllSegments > 10 ? [locationSegments.slice(0, 10), locationSegments.slice(10)] : [locationSegments];
  }, [countAllSegments, locationSegments]);

  if (!locationSegments?.length) {
    return null;
  }

  const listTypeLabel = listType === 'includes' ? t('included') : t('excluded');

  const readOnly = rest.readOnly === true;
  const onSegmentDelete = !readOnly && 'onSegmentDelete' in rest ? rest.onSegmentDelete : undefined;

  return (
    <>
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
        }}
      >
        {listTypeLabel}
      </Typography>

      <Grid container data-test={`${dataTest}-list`}>
        <Grid sx={{ mr: 1.5, display: 'flex', alignItems: 'center' }}>
          {listType === ListType.INCLUDES ? (
            <CheckCircleOutlinedIcon data-test="check-icon" color="success" fontSize="small" />
          ) : (
            <BlockOutlinedIcon data-test="block-icon" color="error" fontSize="small" />
          )}
        </Grid>

        {splitAllSegments[0].map((segment, index) => (
          <LocationSegmentsListItem
            key={segment.id}
            item={segment}
            onItemDelete={onSegmentDelete}
            isLastItem={index === splitAllSegments[0].length - 1 && !showMoreItems}
            dataTest={dataTest}
          />
        ))}

        {showMoreItems &&
          splitAllSegments[1]?.map((segment, index) => (
            <LocationSegmentsListItem
              key={segment.id}
              item={segment}
              onItemDelete={onSegmentDelete}
              isLastItem={index === splitAllSegments[1].length - 1}
              dataTest={dataTest}
            />
          ))}
      </Grid>

      {countAllSegments > 10 && (
        <>
          {!showMoreItems && (
            <Grid container>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >{`and ${splitAllSegments[1]?.length} more.`}</Typography>
            </Grid>
          )}
          <Grid container>
            <Button
              variant="text"
              color="primary"
              onClick={() => setShowMoreItems(!showMoreItems)}
              data-test={'group-add-subgroup-button'}
            >
              {showMoreItems ? t('Hide') : t('Show More')}
            </Button>
          </Grid>
        </>
      )}
    </>
  );
};
